import React, { useEffect, useRef, useState } from "react";
import { getValidYears } from "../../../../utils/financialHelper";
import * as d3 from "d3";
import "./unit.less";
import {
  QuarterDataUnitHoldingPG,
  YearDataUnitHolding,
  QuarterDataUnitHoldingPatternIndus,
  QuarterDataUnitHolding,
  YearDataUnitHoldingPG,
  YearDataUnitHoldingPatternIndus,
  Invit,
} from "../../../../types/invit.types";

interface MetricsRow {
  metric: string;
  values: (string | number)[];
  isHeader?: boolean;
}

interface PieDataItem {
  label: string;
  value: number;
  color: string;
}

interface PatternProps {
  invit: Invit | null;
}

// Helper function to safely convert to number
const toNumber = (
  value: string | number | boolean | null | undefined
): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to format large numbers
const formatNumber = (
  value: string | number | boolean | null | undefined
): string => {
  if (value === null || value === undefined) return "-";
  const num = toNumber(value);
  if (num === 0) return "-";
  return num.toLocaleString("en-IN");
};

const Pattern: React.FC<PatternProps> = ({ invit }) => {
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [unit, setUnit] = useState<"Units Held" | "Total Outstanding Unit (%)">(
    "Units Held"
  );
  const [year, setYear] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  const chartRef = useRef<SVGSVGElement | null>(null);

  const unitHoldingPattern = invit?.unitHoldingPattern || [];
  const years = getValidYears(unitHoldingPattern);

  // Set latest year as default
  useEffect(() => {
    if (years.length > 0 && !year) {
      setYear(years[0]);
    }
  }, [years, year]);

  useEffect(() => {
    if (currentYearData?.quarters.length && view === "Quarterly") {
      setSelectedQuarter(currentYearData.quarters[0].quarter);
    }
  }, [year, view]);

  // Get current year data
  const currentYearData = unitHoldingPattern.find((y) => y.year === year);

  // Helper function to get pie data based on data structure
  const getPieData = (): PieDataItem[] => {
    if (!currentYearData || !currentYearData.quarters) return [];

    let quarterData;

    if (view === "Yearly") {
      // latest quarter for yearly
      quarterData =
        currentYearData.quarters[currentYearData.quarters.length - 1];
    } else {
      // find selected quarter
      quarterData = currentYearData.quarters.find(
        (q) => q.quarter === selectedQuarter
      );
    }

    if (!quarterData) return [];

    // Check data structure type
    if ("unitHoldingPatternIndus" in quarterData) {
      // YearDataUnitHoldingPatternIndus structure
      const data = (quarterData as QuarterDataUnitHoldingPatternIndus)
        .unitHoldingPatternIndus;
      if (!data) return [];

      const sponsorValue = toNumber(data.sponsor?.totalOutstandingUnit?.value);

      // Institutional (sum of relevant fields)
      const institutionalValue =
        toNumber(data.mutualFunds?.totalOutstandingUnit?.value) +
        toNumber(data.insuranceCompany?.totalOutstandingUnit?.value) +
        toNumber(data.foreignPortfolioInvestor?.totalOutstandingUnit?.value) +
        toNumber(data.bank?.totalOutstandingUnit?.value) +
        toNumber(data.pensionFunds?.totalOutstandingUnit?.value);

      // Non-institutional
      const nonInstitutionalValue =
        toNumber(data.individual?.totalOutstandingUnit?.value) +
        toNumber(data.corporateBody?.totalOutstandingUnit?.value) +
        toNumber(data.alternativeInvestmentFund?.totalOutstandingUnit?.value) +
        toNumber(data.trust?.totalOutstandingUnit?.value) +
        toNumber(data.nbfc?.totalOutstandingUnit?.value) +
        toNumber(data.nonResidentIndian?.totalOutstandingUnit?.value) +
        toNumber(data.clearingMember?.totalOutstandingUnit?.value);

      return [
        {
          label: "Sponsor & Sponsor Group",
          value: sponsorValue,
          color: "#1B324C",
        },
        {
          label: "Institutional",
          value: institutionalValue,
          color: "#1F70CC",
        },
        {
          label: "Non-institutional",
          value: nonInstitutionalValue,
          color: "#65D9D1",
        },
      ];
    } else if ("sponsors" in quarterData && "indian" in quarterData) {
      // YearDataUnitHoldingPG structure
      const qData = quarterData as QuarterDataUnitHoldingPG;

      const sponsorValue = toNumber(
        qData.sponsors?.totalOutstandingUnit?.value
      );
      const institutionalValue = toNumber(
        qData.institutions?.subTotal?.totalOutstandingUnit?.value
      );
      const nonInstitutionalValue = toNumber(
        qData.nonInstitutions?.subTotal?.totalOutstandingUnit?.value
      );

      return [
        {
          label: "Sponsor & Sponsor Group",
          value: sponsorValue,
          color: "#1B324C",
        },
        {
          label: "Institutional",
          value: institutionalValue,
          color: "#1F70CC",
        },
        {
          label: "Non-institutional",
          value: nonInstitutionalValue,
          color: "#65D9D1",
        },
      ];
    } else if ("sponsors" in quarterData) {
      // YearDataUnitHolding structure
      const qData = quarterData as QuarterDataUnitHolding;

      const sponsorValue = toNumber(
        qData.sponsors?.totalOutstandingUnit?.value
      );
      const institutionalValue = toNumber(
        qData.institutions?.subTotal?.totalOutstandingUnit?.value
      );
      const nonInstitutionalValue = toNumber(
        qData.nonInstitutions?.subTotal?.totalOutstandingUnit?.value
      );

      return [
        {
          label: "Sponsor & Sponsor Group",
          value: sponsorValue,
          color: "#1B324C",
        },
        {
          label: "Institutional",
          value: institutionalValue,
          color: "#1F70CC",
        },
        {
          label: "Non-institutional",
          value: nonInstitutionalValue,
          color: "#65D9D1",
        },
      ];
    }

    return [];
  };

  const pieData = getPieData();

  const getMetricsData = (): MetricsRow[] => {
    if (!unitHoldingPattern?.length) return [];

    const yearItem = unitHoldingPattern.find((y) => y.year === year) as
      | YearDataUnitHolding
      | YearDataUnitHoldingPatternIndus
      | YearDataUnitHoldingPG;

    if (!yearItem) return [];

    const isRegular = (obj: any): obj is YearDataUnitHolding =>
      obj?.quarters?.[0]?.sponsors !== undefined &&
      obj?.quarters?.[0]?.indian === undefined;
    const isIndus = (obj: any): obj is YearDataUnitHoldingPatternIndus =>
      obj?.quarters?.[0]?.unitHoldingPatternIndus !== undefined;
    const isPG = (obj: any): obj is YearDataUnitHoldingPG =>
      obj?.quarters?.[0]?.indian !== undefined;

    const quarters =
      view === "Yearly"
        ? [yearItem.quarters[yearItem.quarters.length - 1]]
        : yearItem.quarters;

    const metricsArray: MetricsRow[] = [];

    // Helper to add section header
    const addSectionHeader = (headerName: string) => {
      metricsArray.push({
        metric: headerName,
        values: quarters.map(() => ""),
        isHeader: true,
      });
    };

    // Helper to add metric row
    const addMetricRow = (key: string, data: any) => {
      if (!data || !data.unitsHeld) return;

      const values = quarters.map((q, qIdx) => {
        // Get the data for this quarter
        let quarterData = data;

        // For quarterly view, we need to get the correct quarter's data
        if (view === "Quarterly") {
          if (isRegular(yearItem)) {
            const qData = yearItem.quarters[qIdx] as QuarterDataUnitHolding;
            // Navigate to the correct nested property
            const parts = key.split(".");
            let temp: any = qData;
            for (const part of parts) {
              if (temp && temp[part]) {
                temp = temp[part];
              } else {
                return "-";
              }
            }
            quarterData = temp;
          } else if (isIndus(yearItem)) {
            const qData = yearItem.quarters[
              qIdx
            ] as QuarterDataUnitHoldingPatternIndus;
            const parts = key.split(".");
            let temp: any = qData.unitHoldingPatternIndus;
            for (const part of parts) {
              if (temp && temp[part]) {
                temp = temp[part];
              } else {
                return "-";
              }
            }
            quarterData = temp;
          } else if (isPG(yearItem)) {
            const qData = yearItem.quarters[qIdx] as QuarterDataUnitHoldingPG;
            const parts = key.split(".");
            let temp: any = qData;
            for (const part of parts) {
              if (temp && temp[part]) {
                temp = temp[part];
              } else {
                return "-";
              }
            }
            quarterData = temp;
          }
        }

        if (!quarterData || !quarterData.unitsHeld) return "-";

        return unit === "Units Held"
          ? formatNumber(quarterData.unitsHeld.value)
          : formatNumber(quarterData.totalOutstandingUnit.value);
      });

      metricsArray.push({
        metric: data.unitsHeld?.key || key,
        values,
      });
    };

    if (isRegular(yearItem)) {
      const q = quarters[0] as QuarterDataUnitHolding;

      // Sponsors
      addMetricRow("sponsors", q.sponsors);

      // Institutions
      if (q.institutions) {
        addSectionHeader("Institutions");
        Object.entries(q.institutions).forEach(([key, value]) => {
          if (key !== "subTotal") {
            addMetricRow(`institutions.${key}`, value);
          }
        });
        if (q.institutions.subTotal) {
          addMetricRow("institutions.subTotal", q.institutions.subTotal);
        }
      }

      // Non-Institutions
      if (q.nonInstitutions) {
        addSectionHeader("Non-Institutions");
        Object.entries(q.nonInstitutions).forEach(([key, value]) => {
          if (
            key !== "subTotal" &&
            key !== "totalPublicUnitHolding" &&
            key !== "totalUnitsOutstanding"
          ) {
            addMetricRow(`nonInstitutions.${key}`, value);
          }
        });
        if (q.nonInstitutions.subTotal) {
          addMetricRow("nonInstitutions.subTotal", q.nonInstitutions.subTotal);
        }
        if (q.nonInstitutions.totalPublicUnitHolding) {
          addMetricRow(
            "nonInstitutions.totalPublicUnitHolding",
            q.nonInstitutions.totalPublicUnitHolding
          );
        }
        if (q.nonInstitutions.totalUnitsOutstanding) {
          addMetricRow(
            "nonInstitutions.totalUnitsOutstanding",
            q.nonInstitutions.totalUnitsOutstanding
          );
        }
      }
    } else if (isIndus(yearItem)) {
      const q = quarters[0] as QuarterDataUnitHoldingPatternIndus;
      const data = q.unitHoldingPatternIndus;

      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          addMetricRow(key, value);
        });
      }
    } else if (isPG(yearItem)) {
      const q = quarters[0] as QuarterDataUnitHoldingPG;

      // Sponsors
      addMetricRow("sponsors", q.sponsors);

      // Indian
      if (q.indian) {
        addSectionHeader("Indian");
        Object.entries(q.indian).forEach(([key, value]) => {
          if (key !== "subTotal") {
            addMetricRow(`indian.${key}`, value);
          }
        });
        if (q.indian.subTotal) {
          addMetricRow("indian.subTotal", q.indian.subTotal);
        }
      }

      // Foreign
      if (q.foreign) {
        addSectionHeader("Foreign");
        Object.entries(q.foreign).forEach(([key, value]) => {
          if (key !== "subTotal" && key !== "totalUnitholdingOfSponsor") {
            addMetricRow(`foreign.${key}`, value);
          }
        });
        if (q.foreign.subTotal) {
          addMetricRow("foreign.subTotal", q.foreign.subTotal);
        }
        if (q.foreign.totalUnitholdingOfSponsor) {
          addMetricRow(
            "foreign.totalUnitholdingOfSponsor",
            q.foreign.totalUnitholdingOfSponsor
          );
        }
      }

      // Institutions
      if (q.institutions) {
        addSectionHeader("Institutions");
        Object.entries(q.institutions).forEach(([key, value]) => {
          if (key !== "subTotal") {
            addMetricRow(`institutions.${key}`, value);
          }
        });
        if (q.institutions.subTotal) {
          addMetricRow("institutions.subTotal", q.institutions.subTotal);
        }
      }

      // Non-Institutions
      if (q.nonInstitutions) {
        addSectionHeader("Non-Institutions");
        Object.entries(q.nonInstitutions).forEach(([key, value]) => {
          if (
            key !== "subTotal" &&
            key !== "totalPublicUnitHolding" &&
            key !== "totalUnitsOutstanding"
          ) {
            addMetricRow(`nonInstitutions.${key}`, value);
          }
        });
        if (q.nonInstitutions.subTotal) {
          addMetricRow("nonInstitutions.subTotal", q.nonInstitutions.subTotal);
        }
        if (q.nonInstitutions.totalPublicUnitHolding) {
          addMetricRow(
            "nonInstitutions.totalPublicUnitHolding",
            q.nonInstitutions.totalPublicUnitHolding
          );
        }
        if (q.nonInstitutions.totalUnitsOutstanding) {
          addMetricRow(
            "nonInstitutions.totalUnitsOutstanding",
            q.nonInstitutions.totalUnitsOutstanding
          );
        }
      }
    }

    return metricsArray;
  };

  const metricsData = getMetricsData();

  useEffect(() => {
    if (!chartRef.current || pieData.length === 0) return;

    const svgElement = chartRef.current;
    const container = svgElement.parentElement;

    if (!container) return;

    const containerWidth = container.offsetWidth || 240;
    const containerHeight = container.offsetHeight || 240;

    const width = containerWidth;
    const height = containerHeight;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(svgElement);
    svg.selectAll("*").remove();

    svg.attr("width", width).attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const pie = d3
      .pie<PieDataItem>()
      .sort(null)
      .value((d) => d.value);

    const arc = d3
      .arc<d3.PieArcDatum<PieDataItem>>()
      .innerRadius(radius * 0.58)
      .outerRadius(radius * 0.92);

    const arcs = g
      .selectAll("path.slice")
      .data(pie(pieData))
      .enter()
      .append("path")
      .attr("class", "slice")
      .attr("d", arc as any)
      .attr("fill", (d) => d.data.color)
      .style("opacity", 0);

    arcs
      .transition()
      .duration(700)
      .delay((_, i) => i * 120)
      .style("opacity", 1);

    const centerText =
      view === "Yearly" ? `${year}` : `${selectedQuarter} ${year}`;

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.2em")
      .style("font-size", "14px")
      .style("font-weight", 700)
      .style("fill", "#3B3B3B")
      .style("font-family", "'Red Hat Text', sans-serif")
      .text(centerText);
  }, [pieData, view, year]);

  if (!invit || !unitHoldingPattern.length) {
    return (
      <section className="unitholding-pattern">
        <div className="unitholding-pattern__card">
          <p>No unit holding pattern data available</p>
        </div>
      </section>
    );
  }

  return (
    <section className="unitholding-pattern">
      <div className="unitholding-pattern__card">
        {/* Header */}
        <div className="unitholding-pattern__header">
          <h2 className="unitholding-pattern__title">Unitholding Pattern</h2>

          <div className="unitholding-pattern__subheader">
            {/* Toggle */}
            <div className="unitholding-pattern__tabs">
              <button
                className={`unitholding-pattern__tab ${
                  view === "Yearly" ? "unitholding-pattern__tab--active" : ""
                }`}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>

              <button
                className={`unitholding-pattern__tab ${
                  view === "Quarterly" ? "unitholding-pattern__tab--active" : ""
                }`}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>

            {/* Year Dropdown */}
            <div className="unitholding-pattern__dropdown">
              <div className="unitholding-pattern__select">
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="unitholding-pattern__select-input"
                >
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quarter Dropdown — visible only when Quarterly view */}
            {view === "Quarterly" && (
              <div className="unitholding-pattern__dropdown">
                <div className="unitholding-pattern__select">
                  <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                    className="unitholding-pattern__select-input"
                  >
                    {currentYearData?.quarters?.map((q, qIdx) => (
                      <option key={qIdx} value={q.quarter}>
                        {q.quarter}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="unitholding-pattern__body">
          <div className="unitholding-pattern__chart-box">
            <div className="unitholding-pattern__chart-area">
              <svg ref={chartRef} />
            </div>

            <div className="unitholding-pattern__legend">
              {pieData.map((p, i) => (
                <div className="unitholding-pattern__legend-item" key={i}>
                  <span
                    className="unitholding-pattern__legend-dot"
                    style={{ background: p.color }}
                    aria-hidden
                  />
                  <div className="unitholding-pattern__legend-text">
                    <div className="unitholding-pattern__legend-label">
                      {p.label} (%)
                    </div>
                    <div className="unitholding-pattern__legend-value">
                      {p.value.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Units */}
        <div className="unitholding-pattern__units">
          <div className="unitholding-pattern__units-tabs">
            <button
              className={`unitholding-pattern__units-tab ${
                unit === "Units Held"
                  ? "unitholding-pattern__units-tab--active"
                  : ""
              }`}
              onClick={() => setUnit("Units Held")}
            >
              Units Held
            </button>
            <button
              className={`unitholding-pattern__units-tab ${
                unit === "Total Outstanding Unit (%)"
                  ? "unitholding-pattern__units-tab--active"
                  : ""
              }`}
              onClick={() => setUnit("Total Outstanding Unit (%)")}
            >
              Total Outstanding Unit (%)
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="unitholding-pattern__table">
          <table className="unitholding-pattern__table-element">
            <thead className="unitholding-pattern__table-head">
              <tr className="unitholding-pattern__table-row">
                <th className="unitholding-pattern__table-header">
                  Parameters
                </th>

                {view === "Yearly" ? (
                  <th className="unitholding-pattern__table-header">{year}</th>
                ) : (
                  currentYearData?.quarters.map((q, idx) => (
                    <th key={idx} className="unitholding-pattern__table-header">
                      {q.quarter}
                    </th>
                  ))
                )}
              </tr>
            </thead>

            <tbody className="unitholding-pattern__table-body">
              {metricsData.map((row, idx) => (
                <tr
                  key={idx}
                  className={`unitholding-pattern__table-row ${
                    row.isHeader ? "unitholding-pattern__table-row--header" : ""
                  }`}
                >
                  <td
                    className={`unitholding-pattern__table-cell ${
                      row.isHeader
                        ? "unitholding-pattern__table-cell--header"
                        : ""
                    }`}
                  >
                    {row.metric}
                  </td>

                  {row.values.map((val, i) => (
                    <td
                      key={i}
                      className={`unitholding-pattern__table-cell ${
                        row.isHeader
                          ? "unitholding-pattern__table-cell--header"
                          : ""
                      }`}
                    >
                      {val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Pattern;
